import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Layout from '../../components/Layout';
import ClientSearchSelect from '../../components/ao/ClientSearchSelect';
import aoAPI from '../../api/aoAPI';

const CATEGORY_COLORS = {
  Parking: 'bg-blue-50 text-blue-700 border-blue-200',
  Transportation: 'bg-purple-50 text-purple-700 border-purple-200',
  Facilitation: 'bg-amber-50 text-amber-700 border-amber-200',
  DocStamps: 'bg-gray-50 text-gray-700 border-gray-200',
  TaxDocuments: 'bg-orange-50 text-orange-700 border-orange-200',
  RPT: 'bg-red-50 text-red-600 border-red-200',
  Meals: 'bg-green-50 text-green-700 border-green-200',
  Supplies: 'bg-teal-50 text-teal-700 border-teal-200',
  Other: 'bg-gray-50 text-gray-500 border-gray-200',
};

const AODailyExpensesReport = () => {
  const [clients, setClients] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId] = useState('');
  const [clientId, setClientId] = useState('');
  const [groupBy, setGroupBy] = useState('client');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);

    // Load clients once — required by ClientSearchSelect
  useEffect(() => {
    aoAPI.getClients()
      .then(r => setClients(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadStaff();
  }, []);

  useEffect(() => {
    loadReport();
  }, [startDate, endDate, staffId, clientId, groupBy]);

  const loadStaff = async () => {
    try {
      const staff = await aoAPI.getStaff();
      setStaffList(staff.data || []);
    } catch (err) {
      console.error('Failed to load staff:', err);
      setStaffList([]);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = { startDate, endDate, groupBy };
      if (staffId) params.staffId = staffId;
      if (clientId) params.clientId = clientId;

      const res = await aoAPI.getDailyExpensesReport(params);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to load report:', err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const renderSummaryCards = () => {
    if (!report) return null;

    const { totalExpenses, totalEntries, highestExpense } = report.summary;

    return (
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3">
          <p className="text-xs text-teal-600 font-medium">Total Expenses</p>
          <p className="text-xl font-bold text-teal-800 mt-1">{formatCurrency(totalExpenses)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-600 font-medium">Total Entries</p>
          <p className="text-xl font-bold text-gray-800 mt-1">{totalEntries}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-xs text-amber-600 font-medium">Highest Single Expense</p>
          <p className="text-xl font-bold text-amber-800 mt-1">{formatCurrency(highestExpense)}</p>
        </div>
      </div>
    );
  };

  const renderGroupedTable = () => {
    if (!report || !report.groups.length) {
      const label = startDate === endDate
        ? format(new Date(startDate), 'MMMM d, yyyy')
        : `${format(new Date(startDate), 'MMMM d, yyyy')} to ${format(new Date(endDate), 'MMMM d, yyyy')}`;

      return (
        <p className="text-sm text-gray-400 text-center py-12">
          No expenses recorded for {label}.
        </p>
      );
    }

    return (
      <div className="space-y-4">
        {report.groups.map((group, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Group Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-gray-900">{group.groupName}</h3>
                <span className="text-sm font-medium text-teal-600">{formatCurrency(group.totalAmount)}</span>
              </div>
            </div>

            {/* Entries Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {groupBy === 'client' ? 'Staff' : 'Client'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {group.entries.map((entry, entryIdx) => (
                    <tr key={entryIdx}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {format(new Date(entry.date), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.Other}`}>
                          {entry.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {entry.remarks || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {groupBy === 'client' ? entry.staffName : entry.clientName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Category Breakdown */}
            <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(
                  group.entries.reduce((acc, entry) => {
                    acc[entry.category] = (acc[entry.category] || 0) + entry.amount;
                    return acc;
                  }, {})
                ).map(([cat, amt]) => (
                  <span key={cat} className="text-gray-600">
                    {cat} {formatCurrency(amt)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Layout pageTitle="Daily Expenses Report">
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
              <select
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">All Staff</option>
                {staffList.map((staff) => (
                  <option key={staff._id} value={staff._id}>
                    {staff.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <ClientSearchSelect
                clients={clients}
                value={clientId}
                onChange={setClientId}
                placeholder="All Clients"
                allowClear
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setGroupBy('client')}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    groupBy === 'client'
                      ? 'bg-teal-100 text-teal-800 border border-teal-300'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  By Client
                </button>
                <button
                  onClick={() => setGroupBy('staff')}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    groupBy === 'staff'
                      ? 'bg-teal-100 text-teal-800 border border-teal-300'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  By Staff
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {renderSummaryCards()}

        {/* Report Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading report...</p>
          </div>
        ) : (
          renderGroupedTable()
        )}
      </div>
    </Layout>
  );
};

export default AODailyExpensesReport;